import * as React from 'react'

import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
} from 'react-virtualized'
import tinycolor from 'tinycolor2'

import { DeliveryTreeNodeTypes } from '../../enums/DeliveryTreeNodeTypes'
import { IDeliveryTreeNode } from '../../models/IDeliveryTreeNode'
import CompactDeliveryListStore from '../../stores/ui/CompactDeliveryList.store'
import ThemeMode from '../../utils/ThemeModeManager'
import FileInputBase from '../FileInput/FileInput'
import DeliveryNodeRenderer from './DeliveryNodeRenderer'
import DeliveriesTreeStore from './DeliveryTree.store'
import DeliveriesTreeNode from './DeliveryTreeNode'

import Colors from '~/client/src/shared/theme.module.scss'
import './DeliveryTree.scss'

const LEVEL_COLOR_CHANGE_COEFFICIENT = 25
const COLOR_CHANGE_THRESHOLD = 2
const { neutral0, neutral100 } = Colors

interface IDeliveriesTree {
  nodes: IDeliveryTreeNode[]
  startViewDate: Date
  changeContainerExpandState?: (id: string, value: boolean) => void
  compactDeliveryListStore: CompactDeliveryListStore
  CustomNodeWrapperType
  LocationContainerNodeType
  FileInputType: typeof FileInputBase

  className?: string
}
export default class DeliveriesTree extends React.Component<IDeliveriesTree> {
  public static defaultProps = {
    className: 'delivery-tree',
  }

  private deliveriesTreeStore: DeliveriesTreeStore
  private readonly cellMeasurerCache: CellMeasurerCache = null

  public constructor(props: IDeliveriesTree) {
    super(props)

    this.deliveriesTreeStore = new DeliveriesTreeStore(
      props.compactDeliveryListStore,
    )

    this.cellMeasurerCache = new CellMeasurerCache({
      defaultHeight: 60,
      fixedWidth: true,
    })
  }

  public componentDidMount() {
    window.addEventListener('resize', this.clearCellMeasurerCache)
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.clearCellMeasurerCache)
  }

  public componentDidUpdate(prevProps: IDeliveriesTree) {
    if (this.props.nodes !== prevProps.nodes) {
      this.cellMeasurerCache.clearAll()
    }
  }

  public render() {
    const { className, nodes } = this.props
    const flattenedNodes = this.getFlattenedNodes(nodes)

    return (
      <div className={className} style={{ flex: '1 1 auto', height: '100%' }}>
        <AutoSizer>
          {({ width, height }) => (
            <List
              deferredMeasurementCache={this.cellMeasurerCache}
              data={flattenedNodes}
              width={width}
              height={height}
              scrollToAlignment="start"
              rowHeight={this.cellMeasurerCache.rowHeight || 60}
              className="notification-list"
              rowCount={flattenedNodes.length}
              rowRenderer={this.renderRow.bind(this, flattenedNodes)}
            />
          )}
        </AutoSizer>
      </div>
    )
  }

  public renderRow = (
    nodes: IDeliveryTreeNode[],
    { key, style, index, parent }: any,
  ): React.ReactNode => {
    const node = nodes[index]

    if (!node) {
      return null
    }

    const {
      CustomNodeWrapperType,
      LocationContainerNodeType,
      changeContainerExpandState,
      startViewDate,
      FileInputType,
      ...rest
    } = this.props

    const shouldRenderSeparators =
      node.nodeType !== DeliveryTreeNodeTypes.DELIVERY && !!node.level
    const defaultClassName =
      node.nodeType === DeliveryTreeNodeTypes.DELIVERY
        ? 'row'
        : 'row level-tree-node'

    return (
      <CellMeasurer
        cache={this.cellMeasurerCache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {({ measure, registerChild }) => (
          <div
            style={this.getNodeStyle(node, style)}
            ref={registerChild}
            className="col bb-palette-brand-lighter"
          >
            <CustomNodeWrapperType
              nodeType={node.nodeType}
              level={node.level}
              defaultClassName={defaultClassName}
            >
              <DeliveryNodeRenderer
                node={node}
                changeContainerExpandState={changeContainerExpandState}
                startViewDate={startViewDate}
                isContainerUpdateEnabled={false}
                FileInputType={FileInputType}
                className="full-height full-width"
                shouldRenderSeparators={shouldRenderSeparators}
                index={index}
                onChange={this.handleChangeTree}
                measure={measure}
                {...rest}
              >
                <DeliveriesTreeNode
                  node={node}
                  LocationContainerNodeType={LocationContainerNodeType}
                  measureSizeFn={measure}
                />
              </DeliveryNodeRenderer>
            </CustomNodeWrapperType>
          </div>
        )}
      </CellMeasurer>
    )
  }

  private handleChangeTree = (nodes: IDeliveryTreeNode[]) => {
    this.deliveriesTreeStore.handleChangeTree(nodes)
    requestAnimationFrame(() => {
      this.clearCellMeasurerCache()
    })
  }

  private clearCellMeasurerCache = () => {
    this.cellMeasurerCache.clearAll()
  }

  private getFlattenedNodes(nodes: IDeliveryTreeNode[]): IDeliveryTreeNode[] {
    const list = []
    const processNode = (node: IDeliveryTreeNode) => {
      list.push(node)
      if (node.children && node.children.length > 0 && node.state.expanded) {
        node.children.forEach(child => processNode(child as IDeliveryTreeNode))
      }
    }
    nodes.forEach(node => processNode(node))
    return list
  }

  private getNodeStyle(
    { level }: IDeliveryTreeNode,
    style: React.CSSProperties,
  ): React.CSSProperties {
    const backgroundColor = tinycolor(ThemeMode.getHEXColor(neutral0))
      .brighten((level - 1) * LEVEL_COLOR_CHANGE_COEFFICIENT)
      .toString()
    const color = level < COLOR_CHANGE_THRESHOLD ? neutral100 : neutral0

    return { ...style, backgroundColor, color }
  }
}
